import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';
import type { Broadcast } from '../types';

interface UseBroadcastsResult {
  broadcasts: Broadcast[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UseBroadcastsOptions {
  includeRecalled?: boolean;
  limit?: number;
}

export function useBroadcasts({
  includeRecalled = true,
  limit = 100,
}: UseBroadcastsOptions = {}): UseBroadcastsResult {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!client || !ready) return;
    setLoading(true);
    setError(null);
    try {
      let q = client
        .from('broadcasts_admin_view')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (!includeRecalled) {
        q = q.is('recalled_at', null);
      }
      const { data, error: err } = await q;
      if (err) throw err;
      setBroadcasts((data as Broadcast[]) ?? []);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'fetch_broadcasts' },
      });
      setError((err as Error)?.message ?? 'Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  }, [client, ready, includeRecalled, limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!client || !ready) return;
    // UUID-suffixed channel so two instances of useBroadcasts mounted
    // simultaneously (e.g. list + composer preview) don't collide.
    const channelName = `admin-broadcasts-feed:${crypto.randomUUID()}`;
    const channel = client
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'broadcasts' },
        (payload) => {
          const row = payload.new as Broadcast;
          setBroadcasts((prev) => {
            if (prev.some((b) => b.id === row.id)) return prev;
            return [row, ...prev];
          });
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'broadcasts' },
        (payload) => {
          const row = payload.new as Broadcast;
          setBroadcasts((prev) =>
            prev.map((b) => (b.id === row.id ? { ...b, ...row } : b)),
          );
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'broadcasts' },
        (payload) => {
          const old = payload.old as { id?: string };
          if (!old?.id) return;
          setBroadcasts((prev) => prev.filter((b) => b.id !== old.id));
        },
      );
    channel.subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, ready]);

  return { broadcasts, loading, error, refresh };
}
